"use client";

import React, { useEffect, useState } from "react";
import { Cpu, Globe, ShieldCheck, Zap } from "lucide-react";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
interface CompetitionData {
  id: string;
  name: string;
  badgeCode: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
  description: {
    vi: string;
    en: string;
  };
  skills: {
    vi: string[];
    en: string[];
  };
}
const COMPETITIONS: CompetitionData[] = [{
  id: "fll",
  name: "FIRST LEGO League",
  badgeCode: "FLL",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/fll_robotics_competition_lab.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/fll_robotics_competition_lab.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/fll_robotics_competition_lab.png-wvc-sizes",
  imageAlt: "Students testing autonomous LEGO robot on FIRST LEGO League tournament board",
  description: {
    vi: "Đấu trường robotics toàn cầu dành cho học sinh, kết hợp thử thách lập trình robot tự hành với dự án nghiên cứu đổi mới sáng tạo thực tế.",
    en: "A global STEM competition challenging students to build, program autonomous robots and complete real-world innovation projects."
  },
  skills: {
    vi: ["Thiết kế cơ khí / Cơ chế", "Lập trình / Thuật toán", "Cảm biến & Điều khiển", "Chiến thuật & Lập kế hoạch", "Làm việc nhóm"],
    en: ["Mechanical design/mechanisms", "Programming/algorithms", "Sensors and robot control", "Strategy and mission planning", "Teamwork"]
  }
}, {
  id: "wro",
  name: "World Robot Olympiad (WRO)",
  badgeCode: "WRO",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/wro_robotics_tournament_challenge.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/wro_robotics_tournament_challenge.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/wro_robotics_tournament_challenge.png-wvc-sizes",
  imageAlt: "Precision autonomous robot executing missions on World Robot Olympiad challenge track",
  description: {
    vi: "Giải đấu robot quốc tế tập trung vào kỹ năng giải quyết nhiệm vụ chính xác, tối ưu hóa thuật toán và cấu trúc lắp ráp kỹ thuật cao.",
    en: "An international robotics tournament focused on precise mission execution, algorithm optimization, and advanced engineering assembly."
  },
  skills: {
    vi: ["Thiết kế cơ khí / Cơ chế", "Lập trình / Thuật toán", "Cảm biến & Điều khiển", "Chiến thuật & Lập kế hoạch"],
    en: ["Mechanical design/mechanisms", "Programming/algorithms", "Sensors and robot control", "Strategy and mission planning"]
  }
}, {
  id: "vex",
  name: "National VEX Robotics Championship (VEX)",
  badgeCode: "VEX",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/vex_robotics_championship_match.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/vex_robotics_championship_match.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/vex_robotics_championship_match.png-wvc-sizes",
  imageAlt: "Custom metal robot design for VEX Robotics Championship alliance arena match",
  description: {
    vi: "Sân chơi thiết kế robot kim loại và hệ thống điều khiển quy mô lớn, thử thách tư duy chiến thuật đấu trường và phối hợp liên minh.",
    en: "A competition platform centered on metal robotics design, complex control systems, arena strategy, and alliance match play."
  },
  skills: {
    vi: ["Thiết kế cơ khí / Cơ chế", "Lập trình / Thuật toán", "Cảm biến & Điều khiển", "Chiến thuật & Lập kế hoạch", "Làm việc nhóm"],
    en: ["Mechanical design/mechanisms", "Programming/algorithms", "Sensors and robot control", "Strategy and mission planning", "Teamwork"]
  }
}];
const DISCLAIMER = {
  vi: "ROBOTO huấn luyện kỹ thuật độc lập theo chuẩn đề thi các đấu trường mở quốc tế, không tuyên bố là đối tác đại diện chính thức.",
  en: "Independent preparation curriculum benchmarking skills against international tournament formats. Mentions are descriptive of target categories."
};
const SECTION_TITLE = {
  vi: "Hệ sinh thái Cuộc thi Robotics",
  en: "The Robotics Competition Ecosystem"
};
const SECTION_SUBTITLE = {
  vi: "Lộ trình luyện tập kỹ thuật và chuẩn hóa năng lực theo các tiêu chuẩn đấu trường robotics quốc tế",
  en: "Technical training pathway benchmarking core engineering competencies against global tournament formats"
};
const PREP_HEADING = {
  vi: "Định hướng chuẩn bị tại ROBOTO",
  en: "ROBOTO Independent Preparation Path"
};
export default function CompetitionEcosystem() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    const getInitialLang = (): "vi" | "en" => {
      if (typeof window === "undefined") return "vi";
      const saved = localStorage.getItem("roboto_lang");
      if (saved === "vi" || saved === "en") return saved;
      return "vi";
    };
    setLang(getInitialLang());
    const handleLangChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail && (customEvent.detail === "vi" || customEvent.detail === "en")) {
        setLang(customEvent.detail);
      } else {
        const saved = localStorage.getItem("roboto_lang");
        if (saved === "vi" || saved === "en") setLang(saved);
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    window.addEventListener("storage", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
      window.removeEventListener("storage", handleLangChange);
    };
  }, []);
  const handleToggleLang = (newLang: "vi" | "en") => {
    setLang(newLang);
    if (typeof window !== "undefined") {
      localStorage.setItem("roboto_lang", newLang);
      window.dispatchEvent(new CustomEvent("roboto-lang-change", {
        detail: newLang
      }));
    }
  };
  return <section id="competitionecosystem" data-nav="light" className="relative py-16 md:py-24 bg-background text-foreground overflow-hidden">
      {/* Decorative Technical Blueprint Grid Background Pattern */}
      <div className="aria-hidden:true pointer-events-none absolute inset-0 z-0 opacity-15 dark:opacity-10" style={{
      backgroundImage: `
            linear-gradient(to right, var(--border) 1px, transparent 1px),
            linear-gradient(to bottom, var(--border) 1px, transparent 1px)
          `,
      backgroundSize: "40px 40px"
    }} />

      {/* Decorative Blueprint Accent Rings */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-24 z-0 size-96 rounded-full border border-border/40 bg-accent/5 blur-xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -left-24 z-0 size-96 rounded-full border border-border/40 bg-primary/5 blur-xl" />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block with Language Sync Indicator */}
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 pb-12 border-b border-border/60">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted text-muted-foreground text-xs font-mono font-medium mb-4 border border-border">
              <Globe className="size-3.5 text-primary" />
              <span>{lang === "vi" ? "Đấu trường Quốc tế" : "International Tournaments"}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-foreground">
              {SECTION_TITLE[lang]}
            </h2>
            <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-relaxed">
              {SECTION_SUBTITLE[lang]}
            </p>
          </div>

          {/* Minimal Language Switch Control */}
          <div className="inline-flex items-center p-1 rounded-lg bg-muted border border-border shrink-0">
            <Button variant={lang === "vi" ? "default" : "ghost"} size="sm" onClick={() => handleToggleLang("vi")} className="text-xs font-medium h-7 px-3 rounded-md">
              Tiếng Việt
            </Button>
            <Button variant={lang === "en" ? "default" : "ghost"} size="sm" onClick={() => handleToggleLang("en")} className="text-xs font-medium h-7 px-3 rounded-md">
              English
            </Button>
          </div>
        </div>

        {/* 3-Column Competition Grid */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {COMPETITIONS.map((comp, i) => <motion.div key={comp.id} data-index={i} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.35,
          delay: i * 0.06,
          ease: "easeOut"
        }} whileHover={{
          y: -4
        }} className="h-full flex">
              <Card className="flex flex-col justify-between w-full rounded-2xl border border-border/80 bg-card/95 shadow-md hover:shadow-xl transition-[box-shadow,border-color,background-color] duration-300 overflow-hidden relative group">
                {/* Top Subtle Technical Color Accent Line */}
                <div className="h-1.5 w-full bg-gradient-to-r from-primary via-accent to-secondary z-10" />

                {/* Competition Imagery */}
                <div className="relative w-full h-48 overflow-hidden bg-muted border-b border-border/40 shrink-0">
                  <img src={comp.imageSrc} data-wvc-srcset={comp.imageSrcset} data-wvc-sizes={comp.imageSizes} alt={comp.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent opacity-90" />
                </div>

                <div>
                  <CardHeader className="pt-4 pb-4 px-6">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <span className="font-mono text-xs font-bold px-2.5 py-1 rounded bg-muted text-foreground border border-border">
                        {comp.badgeCode}
                      </span>
                      <Zap className="size-4 text-accent" />
                    </div>
                    <CardTitle className="text-xl sm:text-2xl font-serif font-semibold text-foreground leading-snug">
                      {comp.name}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="px-6 space-y-6">
                    {/* Factual Format Description */}
                    <p className="text-sm text-foreground/90 leading-relaxed">
                      {comp.description[lang]}
                    </p>

                    {/* ROBOTO Connection & Independent Preparation Framing */}
                    <div className="rounded-xl bg-muted/60 p-4 border border-border/60 space-y-2">
                      <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
                        <Cpu className="size-4 text-primary shrink-0" />
                        <span>{PREP_HEADING[lang]}</span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed italic">
                        {DISCLAIMER[lang]}
                      </p>
                    </div>

                    {/* Relevant Target Skills */}
                    <div className="space-y-2.5">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        <ShieldCheck className="size-3.5 text-secondary" />
                        <span>
                          {lang === "vi" ? "Kỹ năng trọng tâm" : "Target Competencies"}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {comp.skills[lang].map((skill, skillIdx) => <Badge key={skillIdx} variant="secondary" className="text-xs font-normal py-1 px-2.5 rounded-md border border-border/40">
                            {skill}
                          </Badge>)}
                      </div>
                    </div>
                  </CardContent>
                </div>

                {/* Footer space to keep uniform structure */}
                <div className="px-6 pb-6 pt-2 border-t border-border/40 mt-6 flex items-center justify-between text-xs text-muted-foreground font-mono">
                  <span>ROBOTO ACADEMY</span>
                  <span>STD-CURRICULUM</span>
                </div>
              </Card>
            </motion.div>)}
        </div>
      </div>
    </section>;
}